import { nativeBridge } from "@/frontend/embed/nativeBridge";
import { useParameterStore, ValidationStatus } from "@/frontend/store/ParameterStore";
import { useValidationStore, VALIDATION_STATUS } from "@/frontend/store/ValidationStore";
import { I18n } from "@/i18n/I18n";
import { OPERAND_TYPE } from "@/types/validationTypes";
import { Validation } from "@/backend/models/validation";
import { useShallow } from "zustand/react/shallow";
import { Play } from "@/frontend/SVGs/Play";
import { LoadingSpinner } from "../LoadingSpinner";
import { comparisonCharaterFromRuleType } from "./utils";
import { ValidationsPassingIcon } from "./ValidationsPassingIcon";
import { ValidationsFailedIcon } from "./ValidationsFailedIcon";
import { ValidationsOverriddenIcon } from "./ValidationsOverriddenIcon";

interface ValidationRunProps {
  validation: Validation;
}

export function ValidationRun({ validation }: ValidationRunProps) {
  const parameters = useParameterStore(useShallow(state => state.parameters.filter((p) => p.subjectId === validation.id)));
  const validationStatus = useParameterStore(useShallow(state => state.getValidationStatus(validation.id)));
  const operandB = parameters.find((p) => p.identifier === OPERAND_TYPE.OPERAND_B)?.fields[0];
  const operandA = parameters.find((p) => p.identifier === OPERAND_TYPE.OPERAND_A)?.fields[0];

  const validationState = useValidationStore(state => state.validationStates[validation.id]);
  const isRunning = validationState === VALIDATION_STATUS.RUNNING;
  const rerunValidation = useParameterStore(state => state.rerunValidation);
  // The runner is on the user's machine, so there is nothing to ask in an
  // ordinary browser.
  const canRun = nativeBridge.isAvailable();

  const operandAValue = operandA?.getValue()?.toString() || '';
  const operandBValue = operandB?.getValue()?.toString() || '';
  const comparisonOperator = comparisonCharaterFromRuleType(validation.ruleType);

  return (
    <div>
      <div className='d-flex align-items-center gap-2'>
        {validationStatus === ValidationStatus.FAILING && <ValidationsFailedIcon singleValidation />}
        {validationStatus === ValidationStatus.PASSING && <ValidationsPassingIcon singleValidation />}
        {validationStatus === ValidationStatus.OVERIDDEN && <ValidationsOverriddenIcon />}
        <dl className='d-flex align-items-center gap-2 mb-0'>
          <dt className='visually-hidden'>{I18n.get('inputData')}</dt>
          <dd className='font-monospace small px-2 py-1 mb-0'
              title={operandAValue}>
            {operandAValue || '-'}
          </dd>
          <dt className='visually-hidden'>{I18n.get('operator')}</dt>
          <dd className='font-monospace small text-muted flex-shrink-0 px-1 mb-0'>
            {comparisonOperator}
          </dd>

          <dt className='visually-hidden'>{I18n.get('comparisonValue')}</dt>
          <dd className='font-monospace small px-2 py-1 mb-0 bg-light border rounded shadow-sm'
              title={operandBValue}>
            {operandBValue || '-'}
          </dd>
        </dl>

        {isRunning && (
          <div className="d-flex align-items-center gap-1 ms-2" aria-label={I18n.get('validationRunning')}>
            <LoadingSpinner />
          </div>
        )}
        {!isRunning && canRun && (
          <button
            type='button'
            className='btn btn-link btn-sm p-0 ms-2 d-print-none'
            aria-label={I18n.get('rerunValidation')}
            onClick={() => void rerunValidation(validation.id)}
          >
            <Play />
          </button>
        )}
      </div>
    </div>
  );
}

